import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Button from 'design-system/Button';
import SuccessView from 'design-system/SuccessView';
import StatusBar from 'design-system/StatusBar';
import { ButtonGroup } from 'design-system/ButtonGroup';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserId } from 'reducers/selectors';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import Squircle from 'design-system/Squircle';
import { IconLogout } from 'design-system/icons';
import IconCheck from 'design-system/icons/IconCheck';
import useColors from 'hooks/useColors';
import { navigationRef, popToTop } from 'utils/navigationv6';
import EmptyComponent from 'design-system/EmptyComponent';
import { selectDefaultSpaceId, selectSelectedSpaceInfo, } from './reducers/selectors';
import { deleteSpaceMember, fetchSpacesAction, setSelectedSpace, setShowSpaceLock, } from './actions';
const SpaceUnavailableAccountsMovedScreen = React.memo(({ navigation, route }) => {
    const { spaceInfo } = route.params;
    const colors = useColors();
    const userId = useAppSelector(selectUserId);
    const defaultSpaceId = useAppSelector(selectDefaultSpaceId);
    const selectedSpaceInfo = useAppSelector(selectSelectedSpaceInfo);
    const dispatch = useAppDispatch();
    const isInAvailableSpace = selectedSpaceInfo?.space.isPremium || selectedSpaceInfo?.space.isDefault;
    const isOwner = spaceInfo.space.createdBy === userId;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: EmptyComponent,
            gestureEnabled: false,
            headerBackVisible: false,
        });
    }, []);
    const subtitle = useMemo(() => {
        if (isOwner) {
            return 'Let’s manage your finances in your Private Space';
        }
        return 'To use this space again, ask the owner to upgrade their plan. You can return to your Private Space or leave this space if you no longer need access.';
    }, [isOwner]);
    const [, , isLeaving, fetchLeave] = useFetchOld(muteErrorAndResult);
    const onSwitch = useCallback(() => {
        if (!defaultSpaceId)
            return;
        dispatch(setSelectedSpace(defaultSpaceId));
        navigation.navigateDeprecated('Success', {
            topTitle: 'App changed to:',
            title: 'Private Space',
            subTitle: 'Only assets that you have in this space will be visible in Feed.',
            buttonText: 'Done',
            onContinue: () => {
                popToTop();
                navigationRef.navigateDeprecated('Feed');
                dispatch(setShowSpaceLock(false));
                return true;
            },
        });
    }, [defaultSpaceId]);
    const onLeaveSpace = useCallback(async () => {
        const res = await fetchLeave(deleteSpaceMember(spaceInfo.space.id, userId));
        if (!res)
            return;
        dispatch(fetchSpacesAction());
        navigation.navigateDeprecated('Success', {
            onContinue: () => {
                if (isInAvailableSpace) {
                    popToTop();
                    navigationRef.navigateDeprecated('Feed');
                    dispatch(setShowSpaceLock(false));
                }
                else {
                    navigation.pop();
                    onSwitch();
                }
                return true;
            },
            buttonText: isInAvailableSpace ? 'Done' : 'Switch to Private Space',
            title: `You have left the space "${spaceInfo.space.name}"`,
            subTitle: 'You no longer have access to this space',
            icon: (<Squircle width={88} height={88} color={colors.elements.statusOfflineLight}>
            <IconLogout width={54} height={54} colorKey="statusOffline"/>
          </Squircle>),
        });
    }, [
        colors.elements.statusOfflineLight,
        fetchLeave,
        isInAvailableSpace,
        onSwitch,
        spaceInfo.space.id,
        spaceInfo.space.name,
        userId,
    ]);
    const onDone = useCallback(() => navigation.pop(), []);
    return (<View style={styles.container}>
        <StatusBar barStyle="dark-content" animated/>
        <SuccessView title="Accounts moved" subTitle={subtitle} icon={<Squircle width={88} height={88} color={colors.elements.statusPositiveLight}>
              <IconCheck thick width={54} height={54} color={colors.elements.statusPositive}/>
            </Squircle>}>
          <ButtonGroup type="floating">
            {!isOwner && (<Button title="Remove myself from this space" onPress={onLeaveSpace} isFetching={isLeaving} brandReversed/>)}
            <Button title={isInAvailableSpace ? 'Done' : 'Switch to Private Space'} onPress={isInAvailableSpace ? onDone : onSwitch} brand/>
          </ButtonGroup>
        </SuccessView>
      </View>);
});
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
});
export default SpaceUnavailableAccountsMovedScreen;
